<template>
  <div
    class="flex h-[25%] w-full items-center justify-center rounded-lg bg-white bg-opacity-5 md:h-[95%] md:w-[30%]"
  >
    <button
      @click="captain(player)"
      :class="[
        player ? 'block' : 'hidden',
        captainId === player?.id
          ? 'bg-zinc-900 border-[2px]'
          : 'bg-[#171B36F2] bg-opacity-80',
      ]"
      class="absolute top-[12vh] z-[80] mr-[20%] flex h-[6vmin] w-[6vmin] rotate-45 items-center justify-center rounded-lg border-[1px] border-slate-100 text-[2vw] transition-colors hover:bg-zinc-500"
    >
      <h1
        :class="{
          block: player,
          hidden: !player,
          'text-[0.8vw]': locale === 'ar',
          'text-[2vw]': locale !== 'ar',
        }"
        class="mr-[2%] rotate-[-45deg] text-white"
      >
        {{ locale === "ar" ? "قائد الفريق" : "C" }}
      </h1>
    </button>

    <div
      class="flex h-[95%] w-full flex-row items-center justify-center md:flex-col"
    >
      <div class="flex h-[65%] w-[90%] items-center justify-center">
        <div class="relative flex h-full w-full items-center justify-center">
          <div class="relative flex h-full w-full items-center justify-center">
            <div
              class="relative box-border flex h-[14vmin] w-[14vmin] items-center justify-center overflow-hidden rounded-full border-2 border-white md:h-[24vmin] md:w-[24vmin]"
            >
              <img
                class="h-full w-full object-cover"
                src="../assets/spottyBackground.png"
                alt="Background Image"
              />
              <img
                class="object-stretch absolute h-full w-full"
                :src="player?.image || blankPlayerImage"
                alt="Player Image"
              />
            </div>
          </div>
          <div class="absolute mt-[18.5vh] flex items-center justify-center">
            <img
              class="w-[15vw] min-w-[14rem]"
              src="../assets/banners/playerBanner.svg"
              alt="Player Banner"
            />
            <p
              class="absolute bottom-[13%] w-full text-[0.9vw] font-extrabold text-white"
            >
              {{ formatName(player) || "-" }} 
            </p>
          </div>
        </div>
      </div>

      <div class="flex h-full w-full items-center md:h-[35%] md:flex-col">
        <div class="flex h-full w-full md:flex-col">
          <div
            :class="locale === 'ar' ? 'flex-row-reverse' : ''"
            class="flex h-full w-full items-center justify-evenly"
          >
            <div
              :class="
                locale === 'ar'
                  ? 'md:text-[0.8vw] text-[1.5vw] text-left'
                  : 'md:text-[0.9vw] text-[2vw] text-right'
              "
              class="flex h-full w-[45%] flex-col justify-evenly pr-4 text-left text-saudi-green"
            >
              <div>{{ $t("positions") }}</div>
              <div>{{ $t("caps") }}</div>
              <div>{{ $t("appearances") }}</div>
            </div>
            <div class="h-[90%] w-[0.5%] rounded-full bg-saudi-green"></div>
            <div
              :class="{
                'text-right ': locale === 'ar',
                'text-left md:text-[0.9vw]': locale !== 'ar',
              }"
              class="flex h-full w-[45%] flex-col justify-evenly pl-4 text-[0.7vw] text-white"
            >
              <div>{{ player?.Position || "-" }}</div>
              <div>{{ player?.Caps || "-" }}</div>
              <div>{{ calculateWorldCups(player) || "-" }}</div>
            </div>
          </div>

          <div
            class="flex h-full w-[15%] items-center justify-center md:h-[35%] md:w-full"
          >
            <button
              @click="addPlayerToTeam(player)"
              :disabled="!player"
              :class="
                player ? 'opacity-100' : 'opacity-30 hover:bg-saudi-purple'
              "
              class="h-[80%] w-[80%] rounded-lg bg-saudi-purple text-[0.8vw] font-bold text-white transition-all hover:bg-[#cf70cf]"
            >
              {{ $t("add_player_to_team") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from "vue";
import blankPlayerImage from "../assets/blankPlayer.png";
import { useI18n } from "vue-i18n";

const { locale } = useI18n();

const props = defineProps({
  player: Object,
  playerData: Object,
  captainId: Number,
});

const emit = defineEmits(["addPlayerToTeam", "addCaptain"]);
const correctPlayer = ref(null);
const playerTest = ref(null);

watch(
  () => props.player,
  (newPlayer) => {
    playerTest.value = null;
    correctPlayer.value = newPlayer;
    findPlayer();
  }
);

const findPlayer = () => {
  if (props.player) {
    playerTest.value =
      props.playerData.find((player) => player.id === correctPlayer.value.id) ||
      null;
  }
};

const oldCaptainId = ref(0);

const captain = (player) => {
  if (player.id === oldCaptainId.value) {
    emit("addCaptain", 100);
    oldCaptainId.value = null;
  } else {
    emit("addCaptain", player.id);
    oldCaptainId.value = player.id;

    if (typeof window.gtag === "function") {
      window.gtag("event", "add_captain", {
        event_category: "Player",
        event_label: player.Name,
        player_id: player.id,
        value: 1,
      });
    }
  }
};

const calculateWorldCups = (data) => {
  const worldCupsArray = data?.WorldCups;
  return worldCupsArray
    ? worldCupsArray.map((wc) => "‘" + wc.slice(-2)).join(",")
    : "-";
};

const formatName = (name) => {
  const firstName = name?.Given_name;
  const lastName = name?.Family_name;

  const formattedFirstName = firstName
    ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase()
    : "";
  const formattedLastName = lastName
    ? lastName
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ")
    : "";
  if (locale.value === "ar") {
    return `  ${formattedFirstName} ${formattedLastName}  `.trim();
  } else return `${formattedFirstName} ${formattedLastName}`.trim();
};

const addPlayerToTeam = (player) => {
  emit("addPlayerToTeam", player);

  if (window.gtag) {
    window.gtag("set", "debug_mode", true);

    window.gtag("event", "add_player", {
      event_category: "Player",
      event_label: player.Name,
      player_id: player.id,
      value: 1,
    });
  } else {
    console.error("gtag is not defined");
  }
};
</script>
